import React from 'react';
import { ExternalLink, ArrowUpRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { SHOWCASE_APPS } from '../mock/mock';
import TemplatePreview from './TemplatePreview';

export default function Showcase({ limit }) {
  const navigate = useNavigate();
  const items = limit ? SHOWCASE_APPS.slice(0, limit) : SHOWCASE_APPS;

  const openTemplate = (app) => {
    const params = new URLSearchParams();
    params.set('prompt', app.prompt);
    params.set('template', app.title);
    navigate(`/build?${params.toString()}`);
  };

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
              Start from a template
            </h2>
            <p className="mt-3 text-lg text-neutral-600 max-w-xl">
              Click any template below and Transformerus will start building it for you instantly. No setup needed.
            </p>
          </div>
          <Link
            to="/showcase"
            data-testid="showcase-view-all"
            className="text-sm font-medium text-neutral-900 hover:text-neutral-600 inline-flex items-center gap-1"
          >
            View all <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((app) => (
            <button
              key={app.id}
              type="button"
              onClick={() => openTemplate(app)}
              data-testid={`template-card-${app.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-2xl"
            >
              <div className={`relative aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br ${app.gradient} border border-neutral-200 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-xl`}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.5),transparent_60%)]" />
                <TemplatePreview id={app.id} />
                <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-white/80 backdrop-blur text-[10px] font-medium text-neutral-700 z-10">
                  {app.tag}
                </div>

                {/* hover overlay CTA */}
                <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/15 transition-colors flex items-end justify-center pb-3">
                  <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-200 inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-neutral-900 text-white shadow-lg">
                    Use this template <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold text-neutral-900 group-hover:text-neutral-600 transition-colors truncate">
                    {app.title}
                  </h3>
                  <p className="text-sm text-neutral-500 mt-0.5 line-clamp-2">{app.desc}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 mt-1 text-neutral-400 group-hover:text-neutral-900 group-hover:rotate-12 transition-all flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
